import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface UseUnsavedChangesProps {
  hasUnsavedChanges: boolean;
  onConfirm: () => void;
  onCancel?: () => void;
}

export function useUnsavedChanges({
  hasUnsavedChanges,
  onConfirm,
  onCancel,
}: UseUnsavedChangesProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  // confirmReturn 後に window.history.back() が popstate を再発火させてモーダルが
  // 二重に開かないよう、意図的なナビゲーション中であることを示すフラグ
  const isConfirmNavigatingRef = useRef(false);

  useEffect(() => {
    if (!hasUnsavedChanges) {
      return;
    }

    const handlePopState = () => {
      if (isConfirmNavigatingRef.current) {
        isConfirmNavigatingRef.current = false;
        return;
      }
      setIsModalOpen(true);
    };

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const button = target.closest('button');

      if (button && button.getAttribute('data-nav-target')) {
        event.preventDefault();
        event.stopPropagation();
        setPendingNavigation(button.getAttribute('data-nav-target'));
        setIsModalOpen(true);
      }
    };

    window.history.pushState({ modalOpened: false }, '');

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('click', handleClick, true);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('click', handleClick, true);
    };
  // location.key を依存配列に含めることで、未保存状態のままページ遷移した場合も
  // 新しいページのURLに対してバッファエントリを確実に作成する
  }, [hasUnsavedChanges, location.key]);

  const handleReturn = () => {
    setIsModalOpen(true);
  };

  const confirmReturn = () => {
    setIsModalOpen(false);

    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    } else {
      isConfirmNavigatingRef.current = true;
      // フラグがセットされないまま残らないよう安全のためタイムアウトでリセット
      const CONFIRM_NAVIGATION_TIMEOUT_MS = 500;
      setTimeout(() => {
        isConfirmNavigatingRef.current = false;
      }, CONFIRM_NAVIGATION_TIMEOUT_MS);
      onConfirm();
    }
  };

  const cancelReturn = () => {
    if (onCancel) {
      onCancel();
    }
    setIsModalOpen(false);
    setPendingNavigation(null);
    window.history.pushState({ modalOpened: false }, '');
  };

  return {
    isModalOpen,
    confirmReturn,
    cancelReturn,
    setIsModalOpen,
    handleReturn,
  };
}
